import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, useEffect, useState } from 'react';
import { getAllRoutes } from '@lognai/frontend-core';
import { ThemeProvider } from '@lognai/synapse-design-system-v2';
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import PartnerInviteAcceptPage from './pages/auth/PartnerInviteAcceptPage';
import PartnerInviteExpiredPage from './pages/auth/PartnerInviteExpiredPage';
import SupplierRegistrationPage from './pages/auth/SupplierRegistrationPage';
import WorkspacePage from './pages/WorkspacePage';
import NotFoundPage from './pages/NotFoundPage';
import ProtectedRoute from './components/ProtectedRoute';
import { RoutePermissionGuard } from './components/RoutePermissionGuard';
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import { loadPluginsForUser } from './plugins/loader';
import { useAuth } from './hooks/useAuth';
import { InsightsPage } from './pages/InsightsPage';
import SettingsPage from './pages/SettingsPage';
const MembersPage = lazy(() => import('./pages/members/MembersPage'));

const DraftsPage = lazy(() =>
  import('@lognai/plugin-ai-core-ui').then((m) => ({ default: m.DraftsPage }))
);

// Public (no-login) ad-hoc supplier quote page (token link).
const PublicProcurementQuotePage = lazy(() =>
  import('@lognai/plugin-procurement-ui').then((m) => ({ default: m.PublicQuotePage }))
);

// Global Command Center — full-viewport canvas page (no app chrome).
const CommandCenterPage = lazy(() => import('./pages/command-center/CommandCenterPage'));

// Loading component
const LoadingScreen = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div>Loading...</div>
  </div>
);

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * App Routes Component
 *
 * Handles dynamic plugin loading and route rendering
 */
function AppRoutes() {
  const { user, company, isLoading: authLoading } = useAuth();
  const [pluginsLoaded, setPluginsLoaded] = useState(false);
  const [pluginRoutes, setPluginRoutes] = useState<ReturnType<typeof getAllRoutes>>([]);

  useEffect(() => {
    // Wait for auth to complete before loading plugins
    if (authLoading) return;

    const initPlugins = async () => {
      // Get user roles and company plugins
      const userRoles = user?.roles || [];
      // Prefer backend-computed effective plugins (per active-company context, partner-aware).
      // Fall back to enabledPlugins only if an older API response lacks the field.
      const baseCompanyPlugins = company?.effectivePlugins?.length
        ? company.effectivePlugins
        : (company?.enabledPlugins ?? []);

      // Analytics is a normal per-tenant plugin included by default for non-partner
      // companies. This union is a safety net for API responses that predate the
      // effectivePlugins field; the route/nav is hidden for anyone lacking the permission.
      const isPartner = company?.type === 'PARTNER';
      const companyPlugins = isPartner
        ? baseCompanyPlugins
        : Array.from(new Set([...baseCompanyPlugins, 'analytics']));

      await loadPluginsForUser(userRoles, companyPlugins, {
        isExternalPartner: isPartner,
      });

      // Get registered routes from plugins
      const routes = getAllRoutes();
      setPluginRoutes(routes);
      setPluginsLoaded(true);
    };

    initPlugins();
  }, [authLoading, user?.roles?.join(','), company?.enabledPlugins?.join(','), company?.type]);

  if (authLoading || !pluginsLoaded) {
    return <LoadingScreen />;
  }

  // Render a plugin route's element (handles lazy vs direct components).
  // Wraps in RoutePermissionGuard so URL-typed access is gated even though the
  // sidebar already hides routes the active account can't access.
  const renderPluginElement = (route: ReturnType<typeof getAllRoutes>[number]) => {
    const RouteComponent = typeof route.component === 'function'
      ? lazy(route.component as () => Promise<{ default: React.ComponentType }>)
      : route.component;
    return (
      <RoutePermissionGuard permissions={route.permissions}>
        <Suspense fallback={<LoadingScreen />}>
          <RouteComponent />
        </Suspense>
      </RoutePermissionGuard>
    );
  };

  // Workflow *execution* screens (e.g. pick/pack/ship) are floor-operator,
  // often tablet/handheld, surfaces. They render full-screen — behind auth
  // but WITHOUT the app sidebar/header chrome — for a focused, kiosk-style
  // experience driven by the ExecutionShell component.
  const isFullScreenRoute = (path: string) => path.includes('/workflows/') && path.includes('/execute');
  const fullScreenRoutes = pluginRoutes.filter((r) => isFullScreenRoute(r.path));
  const chromeRoutes = pluginRoutes.filter((r) => !isFullScreenRoute(r.path));

  return (
    <Routes>
      {/* Public routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/partner-invite/accept" element={<PartnerInviteAcceptPage />} />
        <Route path="/partner-invite/expired" element={<PartnerInviteExpiredPage />} />
        <Route path="/supplier-registration" element={<SupplierRegistrationPage />} />
      </Route>

      {/* Public ad-hoc supplier quote (token link, no login, no auth layout) */}
      <Route
        path="/procurement/quote/:token"
        element={
          <Suspense fallback={<LoadingScreen />}>
            <PublicProcurementQuotePage />
          </Suspense>
        }
      />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        {/* Full-screen plugin routes (no app chrome) — matched before the
            chrome layout so their explicit paths win over its catch-all. */}
        {fullScreenRoutes.map((route) => (
          <Route key={route.id} path={route.path} element={renderPluginElement(route)} />
        ))}

        {/* Global Command Center — full-viewport, no MainLayout chrome. */}
        <Route
          path="/command-center"
          element={
            <RoutePermissionGuard permissions={['shipment:shipment:read']}>
              <Suspense fallback={<LoadingScreen />}>
                <CommandCenterPage />
              </Suspense>
            </RoutePermissionGuard>
          }
        />

        <Route element={<MainLayout />}>
          <Route
            path="/"
            element={
              <Navigate
                to={company?.type === 'PARTNER' ? '/vendor-onboarding/my-application' : '/workspace'}
                replace
              />
            }
          />
          {/* The dashboard was retired in favour of the workspace; the path is
              kept as a redirect so bookmarks and "Home" breadcrumbs still land. */}
          <Route path="/dashboard" element={<Navigate to="/workspace" replace />} />
          <Route path="/workspace" element={<WorkspacePage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route
            path="/drafts"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <DraftsPage />
              </Suspense>
            }
          />
          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/companies/:companyId/members"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <MembersPage />
              </Suspense>
            }
          />

          {/* Dynamic plugin routes (with app chrome) */}
          {chromeRoutes.map((route) => (
            <Route key={route.id} path={route.path} element={renderPluginElement(route)} />
          ))}

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark">
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
